import React, { useCallback, useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useHistory, useParams } from 'react-router-dom';
import Button from '~/app/common/Button';
import Loader from '~/app/common/Loader';
import { IssueOrServiceType } from '~/app/data/statuses/chat';
import { route } from '~/app/helpers/route';
import usePageTitle from '~/app/hooks/usePageTitle';
import useTranslation from '~/app/hooks/useTranslation';
import { useAppHeader } from '~/app/modules/appHeader';
import { ROUTES } from '~/app/Router';
import EmptyMessage from '~/issueService/EmptyMessage';
import { useStoreActions, useStoreState } from '~/store';
import { Machine } from '~/types';
import {
  WorkOrder as WorkOrderType,
} from '~/workorder/types';
import { WORKORDER_ROUTES } from '../Router';
import Search from '../Search';
import pageClasses from '../style.module.scss';
import ListItem from './ListItem';
import classes from './style.module.scss';
import { TASK_ROUTES } from '~/tasks/Router';
import { groupWorkOrdersByCreatedAt } from './helper';

export type ListProps = {
  machineView?: boolean;
  listClassName?: string;
};

export type ListComponent = React.FC<ListProps>;

const List: ListComponent = ({ machineView, listClassName }) => {
  const { id } = useParams<{ id: Machine['id'] }>();

  const { t } = useTranslation('workorder.listing');

  const history = useHistory();
  const { setData } = useAppHeader();

  const [workOrders, setWorkOrders] = useState<WorkOrderType[]>([]);

  const machine = useStoreState(state => state.machine.data);
  const fetching = useStoreState(state => state.workOrder.fetching);
  const filters = useStoreState(state => machineView ? state.workOrder.machineFilters : state.workOrder.filters);
  const getWorkOrderHistoryAction = useStoreActions(actions => actions.workOrder.getWorkOrderHistory);

  const [currentPage, setCurrentPage] = useState<number>(1);
  const [hasMore, setHasMore] = useState(true);

  const groups = useMemo(() => groupWorkOrdersByCreatedAt(workOrders), [workOrders]);

  usePageTitle(machineView ? (machine.name || '').toUpperCase() : t('pageTitle'));

  useEffect(() => {
    setData({
      title: t('pageTitle'),
      subTitle: machineView ? `${machine.intern} - ${machine.name}`.toUpperCase() : undefined,
      backLink: machineView ? route(TASK_ROUTES.list) : (history.length > 1 ? history.goBack : route(ROUTES.projects)),
      showUser: false,
      machineId: machineView ? id : undefined,
      projectId: '',
      showMachineMessages: false,
      isIssueDetailPage: false,
      notification: false,
      search: false,
    });
  }, [setData, id, machine, t, history, machineView]);

  const fetchData = useCallback(
    async (page: number = currentPage, more: boolean = hasMore) => {
      if (fetching || !more) return;

      const response = await getWorkOrderHistoryAction({
        machineId: machineView ? id : null,
        filters: filters,
        offset: (page - 1) * 50,
      });

      const { data, success } = response;
      if (!success) {
        return;
      }
      if (page === 1) {
        setWorkOrders(data || []);
      } else {
        setWorkOrders(prev => [...prev, ...(data || [])]);
      }
      if (!data || data.length < 50) {
        setHasMore(false);
      } else {
        setCurrentPage(prev => prev + 1);
      }
    },
    [getWorkOrderHistoryAction, id, machineView, fetching, currentPage, hasMore, filters],
  );

  useEffect(() => {
    fetchData(1, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  return (
    <>
      <Search machineView={!!machineView} />
      {workOrders.length ? (
        <div className={pageClasses['workorder-list']} id="workorder-scrollable-wrapper">
          <InfiniteScroll
            dataLength={workOrders.length}
            next={fetchData}
            hasMore={hasMore}
            loader={fetching ? <Loader /> : null}
            scrollableTarget="workorder-scrollable-wrapper"
          >
            <div className={classes.list}>
              {groups.map(({ group, items }) => (
                <section key={group} className={classes.section}>
                  <h2 className={classes.groupTitle}>{t(`groups.${group}`)}</h2>
                  {items.map(item => (
                    <ListItem key={item.id} item={item} listClassName={listClassName} />
                  ))}
                </section>
              ))}
            </div>
          </InfiniteScroll>
        </div>
      ) : (
        <EmptyMessage type={IssueOrServiceType.ISSUE} />
      )}
      <Button
        to={
          machineView
            ? route(WORKORDER_ROUTES.add, { machineId: id })
            : route(WORKORDER_ROUTES.add)
        }
        color="primary"
        className={classNames(pageClasses.fab, !machineView && pageClasses['fab--above-bottom-nav'])}
        icon="add"
      />
    </>
  );
};

export default List;
